// Compact preset encoder/decoder for shareable URL parameters.
//
// Strategy: short JSON keys + bitmap-packed barsEnabled (1000 bars → ~167 base64 chars).
// URL-safe base64 (RFC 4648 §5): + → -, / → _, no = padding.

function packBits(bools) {
  if (!bools || bools.length === 0) return '';
  const bytes = new Uint8Array(Math.ceil(bools.length / 8));
  for (let i = 0; i < bools.length; i++) {
    if (bools[i]) bytes[i >> 3] |= 1 << (i & 7);
  }
  let bin = '';
  for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]);
  return btoa(bin);
}

function unpackBits(b64, length) {
  if (!b64) return new Array(length).fill(true);
  const bin = atob(b64);
  const out = new Array(length);
  for (let i = 0; i < length; i++) {
    out[i] = !!(bin.charCodeAt(i >> 3) & (1 << (i & 7)));
  }
  return out;
}

function urlSafeB64(b64) {
  return b64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}
function fromUrlSafeB64(s) {
  const std = s.replace(/-/g, '+').replace(/_/g, '/');
  const pad = std.length % 4 === 0 ? '' : '='.repeat(4 - (std.length % 4));
  return std + pad;
}

// Encode a preset / configuration to a URL-safe base64 string.
export function encodePreset(p) {
  const c = {
    v: 1,
    n: p.name || '',
    b: p.bpm,
    s: p.signature,
    p: p.pattern,
    bn: p.barsEnabled?.length || 0,
    bb: packBits(p.barsEnabled || []),
    su: p.subdivision ?? 1,
    so: p.soundType || 'square',
    m: p.mode || 'single',
    ls: p.loopSong !== false,
    ci: p.countInBars || 0,
    r: !!p.rampEnabled,
    rd: p.rampDelta ?? 2,
    re: p.rampEveryBars ?? 4,
    rm: p.rampMaxBpm ?? 300,
    vol: p.volume ?? 0.7,
    pb: p.patternB,
    sb: p.soundTypeB || 'sine',
  };
  if (Array.isArray(p.sections) && p.sections.length > 0) {
    // Strip ids/createdAt to keep payload small
    c.se = p.sections.map((s) => ({
      n: s.name,
      b: s.bpm,
      s: s.signature,
      p: s.pattern,
      nb: s.numBars,
    }));
  }
  const json = JSON.stringify(c);
  return urlSafeB64(btoa(unescape(encodeURIComponent(json))));
}

// Decode a URL-safe base64 preset back to internal shape.
// Throws on invalid input.
export function decodePreset(s) {
  const std = fromUrlSafeB64(s);
  const json = decodeURIComponent(escape(atob(std)));
  const c = JSON.parse(json);
  if (typeof c !== 'object' || !c) throw new Error('invalid preset');

  const preset = {
    name: c.n || 'Preset condiviso',
    bpm: c.b ?? 120,
    signature: c.s || '4/4',
    pattern: Array.isArray(c.p) ? c.p : [2, 1, 1, 1],
    barsEnabled: c.bn > 0 ? unpackBits(c.bb || '', c.bn) : [true],
    subdivision: c.su ?? 1,
    soundType: c.so || 'square',
    mode: c.m === 'song' ? 'song' : c.m === 'polyrhythm' ? 'polyrhythm' : 'single',
    loopSong: c.ls !== false,
    countInBars: c.ci || 0,
    rampEnabled: !!c.r,
    rampDelta: c.rd ?? 2,
    rampEveryBars: c.re ?? 4,
    rampMaxBpm: c.rm ?? 300,
    volume: c.vol ?? 0.7,
    patternB: Array.isArray(c.pb) ? c.pb : [2, 1, 1],
    soundTypeB: c.sb || 'sine',
  };
  if (Array.isArray(c.se)) {
    preset.sections = c.se.map((s, idx) => ({
      id: `${Date.now()}-${idx}-${Math.random().toString(36).slice(2, 6)}`,
      name: s.n || `Sezione ${idx + 1}`,
      bpm: s.b ?? 120,
      signature: s.s || '4/4',
      pattern: Array.isArray(s.p) ? s.p : [2, 1, 1, 1],
      numBars: s.nb ?? 4,
    }));
  } else {
    preset.sections = [];
  }
  return preset;
}

// Build a shareable URL for the current origin + a preset.
export function buildShareUrl(preset) {
  const enc = encodePreset(preset);
  const base = typeof window !== 'undefined'
    ? `${window.location.origin}${window.location.pathname}`
    : '';
  return `${base}?p=${enc}`;
}

// Share via Web Share API on supported devices, fallback to clipboard.
// Returns: 'shared' | 'copied' | 'cancelled' | 'failed'
export async function shareUrl(url, title) {
  try {
    if (typeof navigator !== 'undefined' && navigator.share) {
      try {
        await navigator.share({ title: title || 'Metronomo', url });
        return 'shared';
      } catch (err) {
        if (err && err.name === 'AbortError') return 'cancelled';
        // fall through to clipboard
      }
    }
    if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(url);
      return 'copied';
    }
  } catch {
    /* ignore */
  }
  return 'failed';
}

// Read ?p= from the current URL (returns null if absent or invalid).
export function readPresetFromUrl() {
  if (typeof window === 'undefined') return null;
  const params = new URLSearchParams(window.location.search);
  const p = params.get('p');
  if (!p) return null;
  try {
    return decodePreset(p);
  } catch {
    return null;
  }
}

// Remove ?p= from the URL without reloading.
export function clearPresetFromUrl() {
  if (typeof window === 'undefined') return;
  const url = new URL(window.location.href);
  url.searchParams.delete('p');
  window.history.replaceState({}, '', url.toString());
}
